<template>
  <LazyComponentsLoader
    v-if="statamicComponents"
    :components="statamicComponents"
    :category="category"
    :class="headerFontClass"
  />
  <LazyShopCategoryDefaultExclusiveListing
    v-else-if="isExclusiveListing"
    :category="category"
  />
  <LazyShopCategoryDefaultListing v-else :category="category" />
</template>

<script setup lang="ts">
import {
  getCategoryBreadcrumbs,
  getTranslatedProperty,
} from '@shopware/helpers'

const props = defineProps<{
  navigationId: string
}>()

const route = useRoute()
const { search } = useCategorySearch()
const { locale } = useI18n()
const { user, isLoggedIn } = useUser()
const { isProfessional } = useCustomer(user)
const { getShopRoute } = useShopRouter()

/*
  as cms page always have priority we check if statamic
  overrides a category
*/

const cmsQuery = useAsyncGql({
  operation: 'Category',
  variables: {
    shopwareCategoryId: props.navigationId,
    lang: locale.value,
    status: route.query['live-preview']
      ? '(draft|expired|scheduled|published)'
      : 'published',
  },
})

const shopQuery = useAsyncData('cmsNavigation' + props.navigationId, () =>
  search(props.navigationId, {
    withCmsAssociations: false,
    query: {
      ...route.query,
      associations: {
        media: {},
      },
    },
  })
)

const [cms, shop] = await Promise.all([cmsQuery, shopQuery])

/* shop stuff */

const { category } = useCategory(shop.data)
if (!category.value) {
  throw createError({
    message: 'Shop category not found',
    fatal: true,
    statusCode: 404,
  })
}
provide('category', category)

const cat = category.value.extensions
const isExclusiveBrand = cat?.exclusiveCategory?.isExclusiveBrand
const isExclusiveLanding = isExclusiveBrand && !!cat.categoryManufacturer
const isExclusiveListing = isExclusiveBrand && !cat.categoryManufacturer
const isExclusive = isExclusiveLanding || isExclusiveListing

const accessGranted = computed(() => {
  if (!isExclusive) return true
  if (isLoggedIn.value && isProfessional.value) {
    return user.value?.extensions?.customerCategories?.some(
      (customerCategory: any) =>
        customerCategory.categoryId === category.value.id ||
        customerCategory.categoryId === category.value.parentId
    )
  }
  return false
})

// we are logged in, so navigate to the category entry page (if there is one)
const memberEntry =
  category?.value?.customFields?.novu_jp_custom_categories_entry
if (accessGranted.value && memberEntry) {
  await navigateTo(getShopRoute(`/navigation/${memberEntry}`), {
    replace: true,
    redirectCode: import.meta.env.PROD ? 301 : 303,
  })
}

/* cms stuff */

const statamicEntry = cms.data.value?.entry
const statamicComponents = computed(() => {
  if (statamicEntry?.__typename !== 'Entry_Categories_Category') return null
  return isExclusive && accessGranted.value
    ? statamicEntry.exclusive_replicator_shopware_category
    : statamicEntry.main_replicator_shopware_category
})

/* handle cases with no page to show */

if (isExclusive && !accessGranted.value && !statamicEntry) {
  throw createError({
    message: '[Exclusive] Access denied 🔒',
    statusCode: 403,
    fatal: true,
  })
}

useSeoMeta({
  title:
    getTranslatedProperty(category.value, 'metaTitle') ||
    getTranslatedProperty(category.value, 'name'),
  description:
    getTranslatedProperty(category.value, 'metaDescription') ||
    getTranslatedProperty(category.value, 'description'),
  keywords: getTranslatedProperty(category.value, 'keywords'),
  ogTitle:
    getTranslatedProperty(category.value, 'metaTitle') ||
    getTranslatedProperty(category.value, 'name'),
  ogDescription:
    getTranslatedProperty(category.value, 'metaDescription') ||
    getTranslatedProperty(category.value, 'description'),
})

if (category.value.media) {
  useSeoMeta({
    ogImageUrl: category.value.media.url,
  })
}

useBreadcrumbs(
  !category.value
    ? []
    : getCategoryBreadcrumbs(category.value, { startIndex: 1 })
)

const headerFontClass = computed(() => {
  if (statamicEntry?.__typename !== 'Entry_Categories_Category') return ''
  switch (statamicEntry.title_font?.value) {
    case 'Helvetica-Neue-LT-Pro-Light-Condensed':
      return 'header-font-Helvetica-Neue-LT-Pro-Light-Condensed'
    case 'Helvetica-Neue-Ultra-Light':
      return 'header-font-Helvetica-Neue-Ultra-Light'
    case 'HelveticaNeueLTPro-BdCn':
      return 'header-font-HelveticaNeueLTPro-BdCn'
    case 'Helvetica-Neue-LT-Pro-Th':
      return 'header-font-Helvetica-Neue-LT-Pro-Th'
    case 'Helvetica-Neue-Pro-Bold':
      return 'header-font-Helvetica-Neue-Pro-Bold'
    default:
      return ''
  }
})
</script>

<style scoped lang="css">
.header-font-Helvetica-Neue-LT-Pro-Light-Condensed :deep(h1),
.header-font-Helvetica-Neue-LT-Pro-Light-Condensed :deep(h2),
.header-font-Helvetica-Neue-LT-Pro-Light-Condensed :deep(h3),
.header-font-Helvetica-Neue-LT-Pro-Light-Condensed :deep(h4),
.header-font-Helvetica-Neue-LT-Pro-Light-Condensed :deep(h5),
.header-font-Helvetica-Neue-LT-Pro-Light-Condensed :deep(h6) {
  @apply font-Helvetica-Neue-LT-Pro-Light-Condensed;
}

.header-font-Helvetica-Neue-Ultra-Light :deep(h1),
.header-font-Helvetica-Neue-Ultra-Light :deep(h2),
.header-font-Helvetica-Neue-Ultra-Light :deep(h3),
.header-font-Helvetica-Neue-Ultra-Light :deep(h4),
.header-font-Helvetica-Neue-Ultra-Light :deep(h5),
.header-font-Helvetica-Neue-Ultra-Light :deep(h6) {
  @apply font-Helvetica-Neue-Ultra-Light;
}

.header-font-HelveticaNeueLTPro-BdCn :deep(h1),
.header-font-HelveticaNeueLTPro-BdCn :deep(h2),
.header-font-HelveticaNeueLTPro-BdCn :deep(h3),
.header-font-HelveticaNeueLTPro-BdCn :deep(h4),
.header-font-HelveticaNeueLTPro-BdCn :deep(h5),
.header-font-HelveticaNeueLTPro-BdCn :deep(h6) {
  @apply font-HelveticaNeueLTPro-BdCn;
}

.header-font-Helvetica-Neue-LT-Pro-Th :deep(h1),
.header-font-Helvetica-Neue-LT-Pro-Th :deep(h2),
.header-font-Helvetica-Neue-LT-Pro-Th :deep(h3),
.header-font-Helvetica-Neue-LT-Pro-Th :deep(h4),
.header-font-Helvetica-Neue-LT-Pro-Th :deep(h5),
.header-font-Helvetica-Neue-LT-Pro-Th :deep(h6) {
  @apply font-Helvetica-Neue-LT-Pro;
}

.header-font-Helvetica-Neue-Pro-Bold :deep(h1),
.header-font-Helvetica-Neue-Pro-Bold :deep(h2),
.header-font-Helvetica-Neue-Pro-Bold :deep(h3),
.header-font-Helvetica-Neue-Pro-Bold :deep(h4),
.header-font-Helvetica-Neue-Pro-Bold :deep(h5),
.header-font-Helvetica-Neue-Pro-Bold :deep(h6) {
  @apply font-Helvetica-Neue-Pro-Bold;
}
</style>
